import { Suspense, lazy } from 'react';
import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';
import { AdminRoute, ProtectedRoute } from '@/app/guards';
import { AppShell } from '@/components/layout/AppShell';
import { SkeletonPage } from '@/components/feedback/Skeletons';

/**
 * Route table (specification §12).
 *
 * Every page is code-split; the shell, guards and skeleton stay in the main
 * chunk so the first paint inside Teams is immediate.
 */

const HomePage = lazy(() => import('@/features/home/pages/HomePage'));
const CalendarPage = lazy(() => import('@/features/calendar/pages/CalendarPage'));
const MyReservationsPage = lazy(
  () => import('@/features/reservations/pages/MyReservationsPage'),
);
const RoomsPage = lazy(() => import('@/features/rooms/pages/RoomsPage'));
const RoomDetailPage = lazy(() => import('@/features/rooms/pages/RoomDetailPage'));

const AdminHomePage = lazy(() => import('@/features/admin/pages/AdminHomePage'));
const AdminUsersPage = lazy(() => import('@/features/admin/pages/AdminUsersPage'));
const AdminRoomsPage = lazy(() => import('@/features/admin/pages/AdminRoomsPage'));
const AdminRoomCreatePage = lazy(() => import('@/features/admin/pages/AdminRoomCreatePage'));
const AdminRoomEditPage = lazy(() => import('@/features/admin/pages/AdminRoomEditPage'));
const AdminRoomBlocksPage = lazy(() => import('@/features/admin/pages/AdminRoomBlocksPage'));
const AdminAuditPage = lazy(() => import('@/features/admin/pages/AdminAuditPage'));
const AdminSyncPage = lazy(() => import('@/features/admin/pages/AdminSyncPage'));

const ForbiddenPage = lazy(() => import('@/features/errors/pages/ForbiddenPage'));
const UnavailablePage = lazy(() => import('@/features/errors/pages/UnavailablePage'));
const NotFoundPage = lazy(() => import('@/features/errors/pages/NotFoundPage'));

/** Suspense wrapper so every lazy chunk shows the same skeleton. */
function Lazy({ children }: { children: JSX.Element }): JSX.Element {
  return <Suspense fallback={<SkeletonPage />}>{children}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Lazy><HomePage /></Lazy> },
          { path: 'calendar', element: <Lazy><CalendarPage /></Lazy> },
          { path: 'my-reservations', element: <Lazy><MyReservationsPage /></Lazy> },
          { path: 'rooms', element: <Lazy><RoomsPage /></Lazy> },
          { path: 'rooms/:roomId', element: <Lazy><RoomDetailPage /></Lazy> },
          {
            path: 'admin',
            element: <AdminRoute />,
            children: [
              { index: true, element: <Lazy><AdminHomePage /></Lazy> },
              { path: 'users', element: <Lazy><AdminUsersPage /></Lazy> },
              { path: 'rooms', element: <Lazy><AdminRoomsPage /></Lazy> },
              { path: 'rooms/new', element: <Lazy><AdminRoomCreatePage /></Lazy> },
              { path: 'rooms/:roomId/edit', element: <Lazy><AdminRoomEditPage /></Lazy> },
              { path: 'room-blocks', element: <Lazy><AdminRoomBlocksPage /></Lazy> },
              { path: 'audit', element: <Lazy><AdminAuditPage /></Lazy> },
              { path: 'sincronizacion', element: <Lazy><AdminSyncPage /></Lazy> },
            ],
          },
          { path: '404', element: <Lazy><NotFoundPage /></Lazy> },
          { path: '*', element: <Navigate to="/404" replace /> },
        ],
      },
    ],
  },
  // Access screens live outside the shell: the user may have no session at all.
  { path: '/403', element: <Lazy><ForbiddenPage /></Lazy> },
  { path: '/unavailable', element: <Lazy><UnavailablePage /></Lazy> },
];

export function AppRoutes(): JSX.Element | null {
  return useRoutes(routes);
}
